// /src/components/PlantCard.jsx
import React from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Droplets, Sun, Thermometer, Edit, Trash2 } from 'lucide-react'
import { useAuth } from '../utils/authContext'

const PlantCard = ({ plant, viewMode, showActions = false }) => {
  const { user, isAdmin, isSuperAdmin, canDelete } = useAuth()

  const getCareLevelColor = (level) => {
    switch (level.toLowerCase()) {
      case 'very easy':
        return 'bg-green-100 text-green-700'
      case 'easy':
        return 'bg-primary-100 text-primary-700'
      case 'moderate':
        return 'bg-yellow-100 text-yellow-700'
      case 'difficult':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-forest-100 text-forest-700'
    }
  }

  // Helper function to get the correct image URL
  const getImageUrl = (imageField) => {
    if (!imageField) return '/api/images/placeholder.jpg'
    
    // If it's already a full URL (external image), use it as is
    if (imageField.startsWith('http')) {
      return imageField
    }
    
    // If it's an image key, use our proxy
    if (imageField.includes('/')) {
      return `/api/images/${imageField}`
    }
    
    // Fallback to placeholder
    return '/api/images/placeholder.jpg'
  }

  // Helper function to get the main image from plant data
  const getMainImage = (plant) => {
    // First try to get from images array (new format)
    if (plant.images && Array.isArray(plant.images) && plant.images.length > 0) {
      return getImageUrl(plant.images[0])
    }
    // Fall back to single image field (backward compatibility)
    if (plant.image) {
      return getImageUrl(plant.image)
    }
    // Default fallback
    return '/api/images/placeholder.jpg'
  }

  // Helper function to get total image count
  const getImageCount = (plant) => {
    if (plant.images && Array.isArray(plant.images)) {
      return plant.images.length
    }
    return plant.image ? 1 : 0
  }

  if (viewMode === 'list') {
    return (
      <Link to={`/plant/${plant.id}`} className="block">
        <div className="plant-card">
          <div className="flex items-center space-x-4 p-4">
            <div className="flex-shrink-0 relative">
              <img
                src={getMainImage(plant)}
                alt={plant.name}
                className="w-20 h-20 object-cover rounded-lg"
                onError={(e) => {
                  console.error('Image failed to load:', getMainImage(plant));
                  e.target.src = '/api/images/placeholder.jpg';
                }}
              />
              {getImageCount(plant) > 1 && (
                <div className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  +{getImageCount(plant) - 1}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-forest-900 truncate">
                  {plant.scientificName || plant.name}
                </h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCareLevelColor(plant.careLevel)}`}>
                  {plant.careLevel}
                </span>
              </div>
              <p className="text-sm text-forest-600 italic mb-2">
                {plant.name}
              </p>
              <p className="text-sm text-forest-700 line-clamp-2">
                {plant.description}
              </p>
              <div className="flex items-center space-x-4 mt-3">
                <div className="flex items-center space-x-1 text-xs text-forest-500">
                  <MapPin className="w-3 h-3" />
                  <span>{plant.habitat}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <div className="group relative">
      <Link to={`/plant/${plant.id}`} className="block">
        <div className="plant-card group">
          <div className="aspect-square overflow-hidden relative">
            <img
              src={getMainImage(plant)}
              alt={plant.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                  console.error('Image failed to load:', getMainImage(plant));
                  e.target.src = '/api/images/placeholder.jpg';
                }}
            />
            {getImageCount(plant) > 1 && (
              <div className="absolute top-2 right-2 bg-primary-600 text-white text-xs rounded-full px-2 py-1">
                +{getImageCount(plant) - 1}
              </div>
            )}
            
            {/* Action buttons - only show if showActions is true and user has appropriate permissions */}
            {showActions && (isAdmin() || isSuperAdmin()) && (
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="flex space-x-1">
                  <button
                    className="bg-white/90 hover:bg-white text-forest-700 p-1 rounded-full shadow-sm"
                    title="Edit plant"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // Handle edit - this would be passed as a prop
                    }}
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  {canDelete() && (
                    <button
                      className="bg-red-500/90 hover:bg-red-500 text-white p-1 rounded-full shadow-sm"
                      title="Delete plant"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        // Handle delete - this would be passed as a prop
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-forest-900 truncate">
                {plant.scientificName || plant.name}
              </h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCareLevelColor(plant.careLevel)}`}>
                {plant.careLevel}
              </span>
            </div>
            <p className="text-sm text-forest-600 italic mb-2">
              {plant.name}
            </p>
            <p className="text-sm text-forest-700 line-clamp-2 mb-3">
              {plant.description}
            </p>
            <div className="flex items-center space-x-4 text-xs text-forest-500">
              <div className="flex items-center space-x-1">
                <MapPin className="w-3 h-3" />
                <span>{plant.habitat}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  )
}

export default PlantCard 